/* eslint-disable max-lines */
import React, { useCallback, useMemo, useState } from 'react';

import { gql } from '@apollo/client';
import { useTranslation } from '@modernloop/i18n';
import { FCWithFragments, MaybeTooltip, MoreMenuButton } from '@modernloop/shared/components';
import { IsoTimestamp, assertIsoTimestamp, isAfterNow } from '@modernloop/shared/datetime';
import { useFlag } from '@modernloop/shared/feature-flag';
import { useGenericOnError } from '@modernloop/shared/hooks';
import {
  CopyIcon,
  DescriptionOnIcon,
  EditIcon,
  EventWithCancelIcon,
  LinkIcon,
  OpenInNewTabIcon,
  PhoneWithArrowPointingInwardsIcon,
  PhoneWithArrowPointingOutwardsIcon,
  TextIcon,
} from '@modernloop/shared/icons';
import { GoogleMeetLogo, MicrosoftTeamsLogo, ZoomLogo } from '@modernloop/shared/images';
import { copyRichTextToClipboard } from '@modernloop/shared/utils';
import { Avatar, Box, ListItemIcon, ListItemText, ListSubheader, MenuItem } from '@mui/material';
import { useSnackbar } from 'notistack';

import {
  ApplicationStatus,
  InterviewMeetingLocationType,
  RenderType,
  ScheduleContentInterviewMenu_CandidateEventFragment,
  ScheduleContentInterviewMenu_InterviewFragment,
  useScheduleContentInterviewMenuQuery,
} from 'src/generated/mloop-graphql';

import { getCollaborationService } from 'src/hooks/collaborationService';
import useHistory from 'src/hooks/useHistory';
import useIsApplicationRejected from 'src/hooks/useIsApplicationRejected';
import { LoggerEvent, useLogEvent } from 'src/hooks/useLogEvent';
import useUserId from 'src/hooks/useUserId';

import getUpdateFlowUrl from 'src/urls/getUpdateFlowUrl';

// eslint-disable-next-line modernloop/restrict-imports.cjs
import useCalendarPermissionData from 'src/views-new/Common/CalendarPermissionDialog/useCalendarPermissionData';
// eslint-disable-next-line modernloop/restrict-imports.cjs
import { SidePanelManager } from 'src/views-new/sidepanel/common/SidePanelManager';

import CalendarPermissionDialog from '../ScheduleContentActions/CalendarPermissionDialog';

import ScheduleContentCancelInterviewDialog from './ScheduleContentCancelInterviewDialog';

export enum MenuOptionsType {
  ViewTranscript = 'VIEW_TRANSCRIPT',
  EditInterview = 'EDIT_INTERVIEW',
  CancelInterivew = 'CANCEL_INTERVIEW',
  CopyCodingLink = 'COPY_CODING_LINK',
  CopyMeetingLink = 'COPY_MEETING_LINK',
  CopyMeetingInfo = 'COPY_MEETING_INFO',
  ViewCandidateEvent = 'VIEW_CANDIDATE_EVENT',
  ViewInterviewerEvent = 'VIEW_INTERVIEWER_EVENT',
}

type Props = {
  applicationStageId: string;
  applicationStatus: ApplicationStatus;
  frozenAt?: IsoTimestamp;
};

type Fragments = {
  interview: ScheduleContentInterviewMenu_InterviewFragment;
  candidateEvent: ScheduleContentInterviewMenu_CandidateEventFragment | undefined;
};

const ScheduleContentInterviewMenu: FCWithFragments<Fragments, Props> = ({
  applicationStageId,
  applicationStatus,
  interview,
  candidateEvent,
  frozenAt,
}) => {
  const { t } = useTranslation();
  const handleError = useGenericOnError();
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const { logEvent } = useLogEvent();
  const userId = useUserId();
  const editInterviewUrl = getUpdateFlowUrl(applicationStageId, { stageInterviewId: interview.id });
  const isApplicationRejected = useIsApplicationRejected(applicationStatus);
  const isUpcoming = isAfterNow(assertIsoTimestamp(interview.startAt));
  const showTranscriptItem = useFlag('org_add_interview_bot');

  const [showDeleteInterviewConfirmation, setShowDeleteInterviewConfirmation] = useState(false);
  const [showCalendarPermissionDialog, setShowCalendarPermissionDialog] = useState(false);

  const calendarDisplayData = useCalendarPermissionData({ applicationStageId });

  const { loading: zoomMeetingInfoLoading, data } = useScheduleContentInterviewMenuQuery({
    variables: {
      input: {
        renderType: RenderType.Html,
        id: interview.id,
      },
      zoomMeetingInfoInput: interview.meetingLocation
        ? {
            userId: interview.meetingLocation.details?.zoomInfo?.userId || '',
            joinUrl: interview.meetingLocation.videoMeetingLink || '',
            meetingId: `${interview.meetingLocation.details?.zoomInfo?.meetingId || ''}`,
            password: interview.meetingLocation.details?.zoomInfo?.password || '',
            pstnPassword: interview.meetingLocation.details?.zoomInfo?.pstnPassword || '',
            dialInfo:
              interview.meetingLocation.details?.zoomInfo?.dialInfo?.map((dialInfo) => ({
                type: dialInfo.type,
                number: dialInfo.number || '',
                city: dialInfo.city,
                countryName: dialInfo.countryName,
                country: dialInfo.country,
              })) || [],
          }
        : {
            userId: interview.zoomHostUserId,
            joinUrl: interview.videoMeetingUrl || '',
            meetingId: `${interview.zoomMeetingId || ''}`,
            password: interview.zoomPassword || '',
            pstnPassword: `${interview.zoomPstnPassword || ''}`,
            dialInfo: interview.zoomDialInInfo,
          },
      zoomMeetingUserId: interview.zoomHostUserId || '',
    },
    skip:
      interview.meetingLocation?.type !== InterviewMeetingLocationType.Zoom &&
      interview.videoMeetingUrl?.toLowerCase().indexOf('zoom') === -1,
  });

  const handleMenuOptionSelect = useCallback(
    async (type: MenuOptionsType) => {
      logEvent(LoggerEvent.CLIENT_SCHEDULE_OBJECT_INTERVIEW_MENU_OPTION_SELECTED, {
        userId,
        interviewId: interview.id,
        type,
      });

      switch (type) {
        case MenuOptionsType.ViewTranscript: {
          history.push(`/transcript/${applicationStageId}/${interview.id}`);
          break;
        }
        case MenuOptionsType.EditInterview: {
          if (!calendarDisplayData.hasPermission) {
            setShowCalendarPermissionDialog(true);
            return;
          }

          SidePanelManager.closeSidePanel();
          history.push(editInterviewUrl);
          break;
        }
        case MenuOptionsType.CancelInterivew: {
          setShowDeleteInterviewConfirmation(true);
          break;
        }
        case MenuOptionsType.CopyCodingLink: {
          if (!interview.codingInterviewUrl && !interview.collaborationLink?.url) {
            return;
          }
          try {
            navigator.clipboard.writeText(interview.collaborationLink?.url || interview.codingInterviewUrl || '');
            enqueueSnackbar(t('hiring-dashboard-common:common.toast.codingLinkCopied'), { variant: 'success' });
          } catch {
            enqueueSnackbar(t('hiring-dashboard-schedule:schedule.content.failedToCopyCodingLink'), {
              variant: 'error',
            });
          }
          break;
        }
        case MenuOptionsType.CopyMeetingLink: {
          let copied = false;
          try {
            if (interview.meetingLocation?.videoMeetingLink || interview.meetingLocation?.details?.customLocation) {
              navigator.clipboard.writeText(
                interview.meetingLocation.videoMeetingLink || interview.meetingLocation?.details?.customLocation || ''
              );
              copied = true;
            } else if (interview.videoMeetingUrl) {
              navigator.clipboard.writeText(interview.videoMeetingUrl);
              copied = true;
            }
            if (copied) {
              enqueueSnackbar(t('hiring-dashboard-common:common.toast.meetingLinkCopied'), { variant: 'success' });
            }
          } catch {
            enqueueSnackbar(t('hiring-dashboard-common:common.toast.failedToCopyMeetingLink'), { variant: 'error' });
          }
          break;
        }
        case MenuOptionsType.CopyMeetingInfo: {
          if (!interview.videoMeetingUrl) {
            return;
          }

          try {
            const zoomMeetingInfoHtml = `<div>
                ${data?.templateToken?.ZOOM_MEETING_INFO}
                <br />
                ${data?.templateToken?.ZOOM_MEETING_DIAL_IN_INFO}
              </div>
            `;
            await copyRichTextToClipboard(zoomMeetingInfoHtml);
            enqueueSnackbar(t('hiring-dashboard-common:common.toast.meetingInfoCopied'), { variant: 'success' });
          } catch (error) {
            handleError({ error, errorTitle: t('hiring-dashboard-common:common.toast.failedToCopyMeetingInfo') });
          }
          break;
        }
        case MenuOptionsType.ViewCandidateEvent: {
          if (!candidateEvent?.googleEventUrl) {
            return;
          }
          window.open(candidateEvent?.googleEventUrl, '_blank');
          break;
        }
        case MenuOptionsType.ViewInterviewerEvent: {
          if (!interview.googleEventUrl) {
            return;
          }
          window.open(interview.googleEventUrl, '_blank');
          break;
        }
        default:
      }
    },
    [
      applicationStageId,
      calendarDisplayData.hasPermission,
      candidateEvent?.googleEventUrl,
      data?.templateToken?.ZOOM_MEETING_DIAL_IN_INFO,
      data?.templateToken?.ZOOM_MEETING_INFO,
      editInterviewUrl,
      enqueueSnackbar,
      handleError,
      history,
      interview.codingInterviewUrl,
      interview.collaborationLink?.url,
      interview.googleEventUrl,
      interview.id,
      interview.meetingLocation?.details?.customLocation,
      interview.meetingLocation?.videoMeetingLink,
      interview.videoMeetingUrl,
      logEvent,
      t,
      userId,
    ]
  );

  const editMenuOptions = useMemo(() => {
    if (frozenAt) {
      return null;
    }
    return isUpcoming && !isApplicationRejected ? (
      <MenuItem
        id={MenuOptionsType.EditInterview}
        key={MenuOptionsType.EditInterview}
        data-testid={`schedule-content-interview-menu-item-${MenuOptionsType.EditInterview}`}
        onClick={() => handleMenuOptionSelect(MenuOptionsType.EditInterview)}
      >
        <ListItemIcon>
          <EditIcon />
        </ListItemIcon>
        <ListItemText>{t('hiring-dashboard-schedule:schedule.interviewDetails.editInterview')}</ListItemText>
      </MenuItem>
    ) : (
      <MenuItem
        id={MenuOptionsType.CancelInterivew}
        key={MenuOptionsType.CancelInterivew}
        data-testid={`schedule-content-interview-menu-item-${MenuOptionsType.CancelInterivew}`}
        onClick={() => handleMenuOptionSelect(MenuOptionsType.CancelInterivew)}
      >
        <ListItemIcon>
          <EventWithCancelIcon />
        </ListItemIcon>
        <ListItemText>{t('hiring-dashboard-schedule:schedule.menu.cancelInterview')}</ListItemText>
      </MenuItem>
    );
  }, [frozenAt, handleMenuOptionSelect, isApplicationRejected, isUpcoming, t]);

  const transcriptMenuOption = useMemo(() => {
    if (!showTranscriptItem || (isUpcoming && !isApplicationRejected)) {
      return null;
    }

    return (
      <MenuItem
        id={MenuOptionsType.ViewTranscript}
        key={MenuOptionsType.ViewTranscript}
        data-testid={`schedule-content-interview-menu-item-${MenuOptionsType.ViewTranscript}`}
        onClick={() => handleMenuOptionSelect(MenuOptionsType.ViewTranscript)}
      >
        <ListItemIcon>
          <DescriptionOnIcon />
        </ListItemIcon>
        <ListItemText>{t('hiring-dashboard-schedule:schedule.menu.viewTranscript')}</ListItemText>
      </MenuItem>
    );
  }, [isUpcoming, isApplicationRejected, showTranscriptItem, handleMenuOptionSelect, t]);

  const locationMenuOptions = useMemo(() => {
    // eslint-disable-next-line modernloop/validate-component-definition.cjs
    const getMenuItem = ({
      id,
      text,
      subText,
      icon,
      disabled,
    }: {
      id: MenuOptionsType;
      text: string;
      subText?: string;
      icon: JSX.Element;
      disabled: boolean;
    }) => {
      return (
        <MenuItem
          id={id}
          key={id}
          data-testid={`schedule-content-interview-menu-item-${id}`}
          onClick={() => handleMenuOptionSelect(id)}
          disabled={disabled}
        >
          <ListItemIcon>{icon}</ListItemIcon>
          <ListItemText
            secondary={
              subText ? (
                <MaybeTooltip
                  data-testid={`schedule-content-interview-menu-item-secondary-text-${id}`}
                  title={subText}
                  label={subText}
                  labelProps={{ variant: 'body2' }}
                />
              ) : undefined
            }
          >
            {text}
          </ListItemText>
          <ListItemIcon>
            <CopyIcon />
          </ListItemIcon>
        </MenuItem>
      );
    };

    const jsx: JSX.Element[] = [];

    let meetingHostName = interview.videoMeetingHostEmployee?.fullName || '';

    if (interview.meetingLocation?.hostEmployee?.fullName) {
      meetingHostName = interview.meetingLocation.hostEmployee.fullName;
    } else if (
      interview.meetingLocation?.remoteVideoMeetingHostUser?.firstName ||
      interview.meetingLocation?.remoteVideoMeetingHostUser?.lastName
    ) {
      meetingHostName =
        `${interview.meetingLocation.remoteVideoMeetingHostUser?.firstName} ${interview.meetingLocation.remoteVideoMeetingHostUser?.lastName}`.trim();
    } else if (interview.meetingLocation?.remoteVideoMeetingHostUser?.email) {
      meetingHostName = interview.meetingLocation.remoteVideoMeetingHostUser.email;
    } else if (data?.zoomUsersByIds?.[0]?.firstName || data?.zoomUsersByIds?.[0]?.lastName) {
      meetingHostName = `${data?.zoomUsersByIds?.[0]?.firstName} ${data?.zoomUsersByIds?.[0]?.lastName}`.trim();
    } else if (data?.zoomUsersByIds?.[0]?.email) {
      meetingHostName = data?.zoomUsersByIds?.[0]?.email;
    }

    const meetingHostText = meetingHostName
      ? t('hiring-dashboard-schedule:schedule.menu.host', { name: meetingHostName })
      : undefined;

    if (interview.meetingLocation?.type) {
      switch (interview.meetingLocation.type) {
        case InterviewMeetingLocationType.Zoom: {
          jsx.push(
            <ListSubheader key="list-sub-header-zoom">
              {t('hiring-dashboard-schedule:schedule.content.zoom')}
            </ListSubheader>
          );
          jsx.push(
            getMenuItem({
              id: MenuOptionsType.CopyMeetingLink,
              text: t('hiring-dashboard-schedule:schedule.content.copyMeetingLink'),
              subText: meetingHostText,
              icon: <Avatar src={ZoomLogo} />,
              disabled: false,
            })
          );
          jsx.push(
            getMenuItem({
              id: MenuOptionsType.CopyMeetingInfo,
              text: t('hiring-dashboard-schedule:schedule.content.copyMeetingInfo'),
              subText: meetingHostText,
              icon: <Avatar src={ZoomLogo} />,
              disabled: zoomMeetingInfoLoading,
            })
          );
          break;
        }
        case InterviewMeetingLocationType.GoogleMeet: {
          jsx.push(
            <ListSubheader key="list-sub-header-google-meets">
              {t('hiring-dashboard-schedule:schedule.content.googleMeets')}
            </ListSubheader>
          );
          jsx.push(
            getMenuItem({
              id: MenuOptionsType.CopyMeetingLink,
              text: t('hiring-dashboard-schedule:schedule.content.copyMeetingLink'),
              subText: meetingHostText,
              icon: <Avatar src={GoogleMeetLogo} />,
              disabled: false,
            })
          );
          break;
        }
        case InterviewMeetingLocationType.MicrosoftTeams: {
          jsx.push(
            <ListSubheader key="list-bus-header-microsoft-teams">
              {t('hiring-dashboard-schedule:schedule.content.microsoftTeams')}
            </ListSubheader>
          );
          jsx.push(
            getMenuItem({
              id: MenuOptionsType.CopyMeetingLink,
              text: t('hiring-dashboard-schedule:schedule.content.copyMeetingLink'),
              subText: meetingHostText,
              icon: <Avatar src={MicrosoftTeamsLogo} />,
              disabled: false,
            })
          );
          break;
        }
        case InterviewMeetingLocationType.CustomLink: {
          jsx.push(
            <ListSubheader key="list-bus-header-custom-meeting-link">
              {t('hiring-dashboard-schedule:schedule.menu.customMeetingLink')}
            </ListSubheader>
          );
          jsx.push(
            getMenuItem({
              id: MenuOptionsType.CopyMeetingLink,
              text: t('hiring-dashboard-schedule:schedule.content.copyMeetingLink'),
              icon: <LinkIcon />,
              disabled: false,
            })
          );
          break;
        }
        case InterviewMeetingLocationType.CustomPhone: {
          jsx.push(
            <ListSubheader key="list-bus-header-custom-phone">
              {t('hiring-dashboard-schedule:schedule.menu.customPhone')}
            </ListSubheader>
          );
          jsx.push(
            getMenuItem({
              id: MenuOptionsType.CopyMeetingLink,
              text: t('hiring-dashboard-schedule:schedule.content.copyCustomPhone'),
              icon: <PhoneWithArrowPointingInwardsIcon />,
              disabled: false,
            })
          );
          break;
        }
        case InterviewMeetingLocationType.CandidatePhone: {
          jsx.push(
            <ListSubheader key="list-bus-header-candidate-phone">
              {t('hiring-dashboard-schedule:schedule.menu.candidatePhone')}
            </ListSubheader>
          );
          jsx.push(
            getMenuItem({
              id: MenuOptionsType.CopyMeetingLink,
              text: t('hiring-dashboard-schedule:schedule.content.copyCandidatePhone'),
              icon: <PhoneWithArrowPointingOutwardsIcon />,
              disabled: false,
            })
          );
          break;
        }
        case InterviewMeetingLocationType.CustomText: {
          jsx.push(
            <ListSubheader key="list-bus-header-custom-text">
              {t('hiring-dashboard-schedule:schedule.menu.customText')}
            </ListSubheader>
          );
          jsx.push(
            getMenuItem({
              id: MenuOptionsType.CopyMeetingLink,
              text: t('hiring-dashboard-schedule:schedule.content.copyCustomText'),
              icon: <TextIcon />,
              disabled: false,
            })
          );
          break;
        }
        default:
      }
    } else if (interview.videoMeetingUrl?.toLowerCase().indexOf('zoom') !== -1) {
      jsx.push(
        <ListSubheader key="list-sub-header-zoom">{t('hiring-dashboard-schedule:schedule.content.zoom')}</ListSubheader>
      );
      jsx.push(
        getMenuItem({
          id: MenuOptionsType.CopyMeetingLink,
          text: t('hiring-dashboard-schedule:schedule.content.copyMeetingLink'),
          subText: meetingHostText,
          icon: <Avatar src={ZoomLogo} />,
          disabled: false,
        })
      );
      jsx.push(
        getMenuItem({
          id: MenuOptionsType.CopyMeetingInfo,
          text: t('hiring-dashboard-schedule:schedule.content.copyMeetingInfo'),
          subText: meetingHostText,
          icon: <Avatar src={ZoomLogo} />,
          disabled: zoomMeetingInfoLoading,
        })
      );
    } else if (interview.videoMeetingUrl?.toLowerCase().indexOf('meet.google.com') !== -1) {
      jsx.push(
        <ListSubheader key="list-sub-header-google-meets">
          {t('hiring-dashboard-schedule:schedule.content.googleMeets')}
        </ListSubheader>
      );
      jsx.push(
        getMenuItem({
          id: MenuOptionsType.CopyMeetingLink,
          text: t('hiring-dashboard-schedule:schedule.content.copyMeetingLink'),
          subText: meetingHostText,
          icon: <Avatar src={GoogleMeetLogo} />,
          disabled: false,
        })
      );
    } else if (interview.videoMeetingUrl?.toLowerCase().indexOf('teams.microsoft.com') !== -1) {
      jsx.push(
        <ListSubheader key="list-bus-header-microsoft-teams">
          {t('hiring-dashboard-schedule:schedule.content.microsoftTeams')}
        </ListSubheader>
      );
      jsx.push(
        getMenuItem({
          id: MenuOptionsType.CopyMeetingLink,
          text: t('hiring-dashboard-schedule:schedule.content.copyMeetingLink'),
          subText: meetingHostText,
          icon: <Avatar src={MicrosoftTeamsLogo} />,
          disabled: false,
        })
      );
    } else if (interview.videoMeetingUrl?.length) {
      jsx.push(
        <ListSubheader key="list-bus-header-custom-meeting-link">
          {t('hiring-dashboard-schedule:schedule.menu.customMeetingLink')}
        </ListSubheader>
      );
      jsx.push(
        getMenuItem({
          id: MenuOptionsType.CopyMeetingLink,
          text: t('hiring-dashboard-schedule:schedule.content.copyMeetingLink'),
          icon: <LinkIcon />,
          disabled: false,
        })
      );
    }

    return jsx;
  }, [
    data?.zoomUsersByIds,
    handleMenuOptionSelect,
    interview.meetingLocation,
    interview.videoMeetingHostEmployee?.fullName,
    interview.videoMeetingUrl,
    t,
    zoomMeetingInfoLoading,
  ]);

  const calendarMenuOptions = useMemo(() => {
    const getMenuItem = (id: MenuOptionsType, text: string, icon: JSX.Element) => {
      return (
        <MenuItem
          id={id}
          key={id}
          data-testid={`schedule-content-interview-menu-item-${id}`}
          onClick={() => handleMenuOptionSelect(id)}
        >
          <ListItemIcon>{icon}</ListItemIcon>
          <ListItemText>{text}</ListItemText>
          <ListItemIcon>
            <OpenInNewTabIcon />
          </ListItemIcon>
        </MenuItem>
      );
    };

    const jsx: JSX.Element[] = [];

    if (interview.googleEventUrl?.indexOf('google') !== -1) {
      jsx.push(
        <ListSubheader key="list-bus-header-google calendar">
          {t('hiring-dashboard-schedule:schedule.menu.googleCalendar')}
        </ListSubheader>
      );
    } else if (interview.googleEventUrl?.indexOf('outlook') !== -1) {
      jsx.push(
        <ListSubheader key="list-bus-header-outlook">
          {t('hiring-dashboard-schedule:schedule.menu.outlook')}
        </ListSubheader>
      );
    }

    if (!interview.isHiddenFromCandidate) {
      if (candidateEvent?.googleEventUrl?.indexOf('google') !== -1) {
        jsx.push(
          getMenuItem(
            MenuOptionsType.ViewCandidateEvent,
            t('hiring-dashboard-schedule:schedule.content.viewCandidateEvent'),
            <Avatar src="/static/images/integrations/google-calendar.png" />
          )
        );
      } else if (candidateEvent?.googleEventUrl?.indexOf('outlook') !== -1) {
        jsx.push(
          getMenuItem(
            MenuOptionsType.ViewCandidateEvent,
            t('hiring-dashboard-schedule:schedule.content.viewCandidateEvent'),
            <Avatar src="/static/images/integrations/outlook-calendar.png" />
          )
        );
      }
    }

    if (interview.googleEventUrl?.indexOf('google') !== -1) {
      jsx.push(
        getMenuItem(
          MenuOptionsType.ViewInterviewerEvent,
          t('hiring-dashboard-schedule:schedule.menu.viewInterviewerEvent'),
          <Avatar src="/static/images/integrations/google-calendar.png" />
        )
      );
    } else if (interview.googleEventUrl?.indexOf('outlook') !== -1) {
      jsx.push(
        getMenuItem(
          MenuOptionsType.ViewInterviewerEvent,
          t('hiring-dashboard-schedule:schedule.menu.viewInterviewerEvent'),
          <Avatar src={MicrosoftTeamsLogo} />
        )
      );
    }

    return jsx;
  }, [
    candidateEvent?.googleEventUrl,
    handleMenuOptionSelect,
    interview.googleEventUrl,
    interview.isHiddenFromCandidate,
    t,
  ]);

  const codingLinkMenuOption = useMemo(() => {
    const jsx: JSX.Element[] = [];
    let iconUrl = '';

    if (interview.collaborationLink) {
      const collaborationToolService = getCollaborationService(interview.collaborationLink.collaborationToolType);
      if (!collaborationToolService) {
        return null;
      }

      jsx.push(
        <ListSubheader key="list-sub-header-copy-coding-link">
          {t('hiring-dashboard-schedule:schedule.menu.codingLink')}
        </ListSubheader>
      );
      jsx.push(
        <MenuItem
          id={MenuOptionsType.CopyCodingLink}
          key={MenuOptionsType.CopyCodingLink}
          data-testid={`schedule-content-interview-menu-item-${MenuOptionsType.CopyCodingLink}`}
          onClick={() => handleMenuOptionSelect(MenuOptionsType.CopyCodingLink)}
        >
          <ListItemIcon>
            <Avatar src={collaborationToolService?.logoUrl} />
          </ListItemIcon>
          <ListItemText>{t('hiring-dashboard-schedule:schedule.menu.copyCodingLink')}</ListItemText>
        </MenuItem>
      );
    } else if (interview.codingInterviewUrl) {
      if (interview.codingInterviewUrl.indexOf('coderpad') !== -1) {
        iconUrl = '/static/images/integrations/CoderPad.jpeg';
      } else if (interview.codingInterviewUrl.indexOf('codesignal') !== -1) {
        iconUrl = '/static/images/integrations/codesignal.png';
      } else if (interview.codingInterviewUrl.indexOf('codility') !== -1) {
        // Example Codility link - https://app.codility.com/test/XYZ123/",
        iconUrl = '/static/images/integrations/codility.png';
      } else if (interview.codingInterviewUrl.indexOf('hr.gs') !== -1) {
        // Example HackerRank link - https://hr.gs/123456
        iconUrl = '/static/images/integrations/hackerrank.png';
      }

      jsx.push(
        <ListSubheader key="list-sub-header-copy-coding-link">
          {t('hiring-dashboard-schedule:schedule.menu.codingLink')}
        </ListSubheader>
      );
      jsx.push(
        <MenuItem
          id={MenuOptionsType.CopyCodingLink}
          key={MenuOptionsType.CopyCodingLink}
          data-testid={`schedule-content-interview-menu-item-${MenuOptionsType.CopyCodingLink}`}
          onClick={() => handleMenuOptionSelect(MenuOptionsType.CopyCodingLink)}
        >
          <ListItemIcon>
            <Avatar src={iconUrl} />
          </ListItemIcon>
          <ListItemText>{t('hiring-dashboard-schedule:schedule.menu.copyCodingLink')}</ListItemText>
        </MenuItem>
      );
    }

    return jsx;
  }, [handleMenuOptionSelect, interview.codingInterviewUrl, interview.collaborationLink, t]);

  return (
    <Box key="schedule-content-interview-menu-button">
      <MoreMenuButton data-testid="schedule-content-interview-menu-button" sx={{ width: '320px' }}>
        {editMenuOptions}

        {transcriptMenuOption}

        {locationMenuOptions}

        {calendarMenuOptions}

        {codingLinkMenuOption}
      </MoreMenuButton>

      {showDeleteInterviewConfirmation && (
        <ScheduleContentCancelInterviewDialog
          applicationStageInterviewId={interview.id}
          onClose={() => setShowDeleteInterviewConfirmation(false)}
        />
      )}

      {showCalendarPermissionDialog && (
        <CalendarPermissionDialog
          fullName={calendarDisplayData.scheduler?.fullName || ''}
          imageUrl={calendarDisplayData.scheduler?.internalProfilePictureUrl || ''}
          email={calendarDisplayData.scheduler?.email || ''}
          candidateCalendarLabel={calendarDisplayData.candidateCalendar}
          candidateId={calendarDisplayData.candidateId}
          candidateEventCalendarId={calendarDisplayData.candidateEventCalendarId}
          interviewerEventCalendarId={calendarDisplayData.interviewerEventCalendarId}
          candidateCalendarFound={calendarDisplayData.candidateCalendarFound}
          interviewerCalendarLabel={calendarDisplayData.interviewerCalendar}
          interviewerCalendarFound={calendarDisplayData.interviewerCalendarFound}
          onClose={() => setShowCalendarPermissionDialog(false)}
        />
      )}
    </Box>
  );
};

ScheduleContentInterviewMenu.fragments = {
  interview: gql`
    fragment ScheduleContentInterviewMenu_interview on ApplicationStageInterview {
      id
      startAt
      googleEventUrl
      videoMeetingUrl
      zoomHostUserId
      zoomDialInInfo
      zoomMeetingId
      zoomPassword
      zoomPstnPassword
      codingInterviewUrl
      isHiddenFromCandidate
      videoMeetingHostEmployee {
        id
        fullName
      }
      meetingLocation {
        id
        type
        videoMeetingLink
        hostEmployee {
          id
          fullName
        }
        remoteVideoMeetingHostUser {
          ... on ZoomUser {
            zoomUserId
            firstName
            lastName
            email
          }
        }
        details {
          customLocation
          zoomInfo {
            meetingId
            userId
            password
            pstnPassword
            dialInfo {
              type
              number
              city
              countryName
              country
            }
          }
        }
      }
      collaborationLink {
        id
        collaborationToolType
        url
      }
    }
  `,
  candidateEvent: gql`
    fragment ScheduleContentInterviewMenu_candidateEvent on ApplicationStageCandidateEvent {
      id
      googleEventUrl
    }
  `,
};

export default ScheduleContentInterviewMenu;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const ScheduleContentInterviewMenuQuery = gql`
  query ScheduleContentInterviewMenu(
    $input: TemplateTokenInput!
    $zoomMeetingInfoInput: TokenZoomMeetingInput!
    $zoomMeetingUserId: String!
  ) {
    templateToken(input: $input) {
      ZOOM_MEETING_INFO(input: $zoomMeetingInfoInput)
      ZOOM_MEETING_DIAL_IN_INFO(input: $zoomMeetingInfoInput)
    }
    zoomUsersByIds(input: { zoomUserIds: [$zoomMeetingUserId] }) {
      zoomUserId
      firstName
      lastName
      email
    }
  }
`;
